import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import SearchBar from '../components/SearchBar';
import ToolCard from '../components/ToolCard';
import Sidebar from '../components/Sidebar';
import LoginSignupModal from '../components/LoginSignupModal';
import { useUser } from '../UserContext';

const HomePage = () => {
  const [tools, setTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Fetching data...");
        const toolsCollection = collection(db, 'tools');
        const categoriesCollection = collection(db, 'toolCategories');
        
        const [toolsSnapshot, categoriesSnapshot] = await Promise.all([
          getDocs(toolsCollection),
          getDocs(categoriesCollection)
        ]);

        console.log("Tools snapshot size:", toolsSnapshot.size);
        console.log("Categories snapshot size:", categoriesSnapshot.size);

        const categoriesList = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          icon: doc.data().icon
        }));
        setCategories(categoriesList);
        console.log("Categories set:", categoriesList);

        const toolsList = toolsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          categoryName: categoriesList.find(cat => cat.id === doc.data().category)?.name || 'Unknown Category'
        }));
        setTools(toolsList);
        setFilteredTools(toolsList);
        console.log("Tools set:", toolsList);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data: ", err);
        setError("There was an issue loading the data.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (searchTerm) => {
    filterTools(searchTerm, selectedCategories, availabilityFilter);
  };

  const handleCategoryChange = (categoryId) => {
    const updatedCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(c => c !== categoryId)
      : [...selectedCategories, categoryId];
    setSelectedCategories(updatedCategories);
    filterTools('', updatedCategories, availabilityFilter);
  };

  const handleAvailabilityChange = (availability) => {
    setAvailabilityFilter(availability);
    filterTools('', selectedCategories, availability);
  };

  const filterTools = (searchTerm, categoryIds, availability) => {
    console.log("Filtering tools:", { searchTerm, categoryIds, availability });
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    const filtered = tools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(lowercasedSearchTerm) ||
        (tool.description && tool.description.toLowerCase().includes(lowercasedSearchTerm));
      
      const matchesCategory = categoryIds.length === 0 || categoryIds.includes(tool.category);

      const matchesAvailability = availability === 'all' ||
        (availability === 'available' && tool.available) ||
        (availability === 'unavailable' && !tool.available);

      return matchesSearch && matchesCategory && matchesAvailability;
    });
    console.log("Filtered tools:", filtered);
    setFilteredTools(filtered);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLoginRequired = () => {
    setIsModalOpen(true);
  };

  console.log("Rendering HomePage. User:", user);
  console.log("Filtered tools:", filteredTools);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row">
        <button
          className="md:hidden mb-4 bg-blue-500 text-white px-4 py-2 rounded"
          onClick={toggleSidebar}
        >
          {sidebarOpen ? 'Close Filters' : 'Open Filters'}
        </button>
        <div className={`w-full md:w-1/4 ${sidebarOpen ? 'block' : 'hidden'} md:block`}>
          <Sidebar 
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoryChange}
            availabilityFilter={availabilityFilter}
            onAvailabilityChange={handleAvailabilityChange}
          />
        </div>
        <div className="w-full md:w-3/4 md:ml-4">
          <p className="text-lg mb-4">
            {!user && "Log in or sign up to borrow tools!"}
          </p>
          <SearchBar onSearch={handleSearch} />
          {loading && <div className="text-center mt-8">Loading tools...</div>}
          {error && <div className="text-center mt-4 mb-4 text-yellow-600">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTools.map(tool => (
              <ToolCard 
                key={tool.id} 
                tool={tool} 
                isLoggedIn={!!user} 
                onLoginRequired={handleLoginRequired}
              />
            ))}
          </div>
          {!loading && filteredTools.length === 0 && (
            <div className="text-center mt-8">No tools found matching your criteria.</div>
          )}
        </div>
      </div>
      <LoginSignupModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default HomePage;
